import { create } from 'zustand';
import {
    DashboardStatistics,
    DashboardByBranchResponse,
    StatisticType,
} from '../types/dashboard.type';
import {
    getDashboardStatistics,
    getDashboardOverview,
    getDashboardByBranch,
} from '../services/dashboard.service';
import { OrganizationType } from '../types/organization.type';
import {
    getCurrentDashboardMonthValue,
    resolveDashboardMonthValue,
    buildDashboardMonthOptions,
} from '../utils/dashboardMonth.helpers';

interface DashboardState {
    statistics: DashboardStatistics | null;
    aggregatedStatistics: DashboardStatistics | null;
    byBranch: DashboardByBranchResponse | null;
    selectedStats: StatisticType[];
    isLoading: boolean;
    isLoadingAggregated: boolean;
    isLoadingByBranch: boolean;
    error: string | null;
    aggregatedError: string | null;
    byBranchError: string | null;
    selectedMonth: string;
    /** When this browser last received the monthly / lifetime statistics (ms epoch). */
    statisticsFetchedAt: number | null;
    aggregatedFetchedAt: number | null;
    fetchDashboardStatistics: (
        orgId: string,
        orgType: OrganizationType,
        monthDate?: string
    ) => Promise<void>;
    fetchAggregatedDashboardStatistics: (
        orgId: string,
        orgType: OrganizationType
    ) => Promise<void>;
    fetchDashboardByBranch: (
        orgId: string,
        monthDate?: string
    ) => Promise<void>;
    clearAggregatedDashboard: () => void;
    setSelectedStats: (stats: StatisticType[]) => void;
    setSelectedMonth: (month: string) => void;
    saveDashboardPreferences: () => void;
    loadDashboardPreferences: () => void;
}

const useDashboardStore = create<DashboardState>((set, get) => ({
    statistics: null,
    aggregatedStatistics: null,
    byBranch: null,
    selectedStats: [],
    isLoading: false,
    isLoadingAggregated: false,
    isLoadingByBranch: false,
    error: null,
    aggregatedError: null,
    byBranchError: null,
    selectedMonth: getCurrentDashboardMonthValue(),
    statisticsFetchedAt: null,
    aggregatedFetchedAt: null,

    fetchDashboardStatistics: async (
        orgId: string,
        orgType: OrganizationType,
        monthDate?: string
    ) => {
        try {
            set({ isLoading: true, error: null });
            const response = await getDashboardStatistics(
                orgId,
                orgType,
                monthDate || get().selectedMonth
            );
            const payload = response as DashboardStatistics | undefined; 
            
            if (!payload) {
                set({
                  statistics: null,
                  isLoading: false,
                  error: 'Dashboard payload is empty',
                });
                return;
              }

            set({ statistics: payload, isLoading: false, statisticsFetchedAt: Date.now() });


        } catch (error) {
            set({
                error: error instanceof Error 
                    ? error.message 
                    : 'Failed to fetch dashboard statistics',
                isLoading: false
            });
        }
    },

    fetchAggregatedDashboardStatistics: async (
        orgId: string,
        orgType: OrganizationType
    ) => {
        try {
            set({ isLoadingAggregated: true, aggregatedError: null });

            const response = await getDashboardOverview(orgId, orgType);
            const payload = response as DashboardStatistics | undefined;
            if (!payload) {
                set({
                    aggregatedStatistics: null,
                    isLoadingAggregated: false,
                    aggregatedError: 'Dashboard overview payload is empty',
                });
                return;
            }
            set({
                aggregatedStatistics: payload,
                isLoadingAggregated: false,
                aggregatedError: null,
                aggregatedFetchedAt: Date.now(),
            });
        } catch (error) {
            set({
                aggregatedStatistics: null,
                isLoadingAggregated: false,
                aggregatedError:
                    error instanceof Error
                        ? error.message
                        : 'Failed to load organization overview',
            });
        }
    },

    clearAggregatedDashboard: () => {
        set({
            aggregatedStatistics: null,
            aggregatedError: null,
            isLoadingAggregated: false,
        });
    },

    fetchDashboardByBranch: async (orgId: string, monthDate?: string) => {
        try {
            set({ isLoadingByBranch: true, byBranchError: null });
            const response = await getDashboardByBranch(
                orgId,
                monthDate || get().selectedMonth
            );
            const payload = response as DashboardByBranchResponse | undefined;

            if (!payload) {
                set({
                    byBranch: null,
                    isLoadingByBranch: false,
                    byBranchError: 'Branch analysis payload is empty',
                });
                return;
            }

            set({ byBranch: payload, isLoadingByBranch: false, byBranchError: null });
        } catch (error) {
            set({
                byBranch: null,
                isLoadingByBranch: false,
                byBranchError:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch branch analysis',
            });
        }
    },

    setSelectedStats: (stats: StatisticType[]) => {
        set({ selectedStats: stats });
        get().saveDashboardPreferences();
    },

    setSelectedMonth: (month: string) => {
        set({ selectedMonth: month });
        get().saveDashboardPreferences();
    },

    saveDashboardPreferences: () => {
        const { selectedStats, selectedMonth } = get();
        sessionStorage.setItem('dashboardPreferences', JSON.stringify({
            selectedStats,
            selectedMonth
        }));
    },

    loadDashboardPreferences: () => {
        const storedPrefs = sessionStorage.getItem('dashboardPreferences');
        const monthOptions = buildDashboardMonthOptions();
        const defaultMonth = getCurrentDashboardMonthValue();
        if (storedPrefs) {
            const { selectedStats, selectedMonth } = JSON.parse(storedPrefs);
            set({
                selectedStats: selectedStats && selectedStats.length > 0 ? selectedStats : get().selectedStats,
                selectedMonth: resolveDashboardMonthValue(selectedMonth, monthOptions),
            });
        } else {
            set({ selectedMonth: defaultMonth });
        }
    },
}));

export { useDashboardStore };